import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatNumber } from '@/i18n/format';
import { SkuExcel } from './SkuExcel';
import type { TranslationKey } from '@/i18n/dictionaries/en';

type Step = 'upload' | 'mapping' | 'validate' | 'dryrun' | 'commit' | 'summary';
const STEPS: { key: Step; labelKey: TranslationKey }[] = [
  { key: 'upload', labelKey: 'importWizard.stepUpload' },
  { key: 'mapping', labelKey: 'importWizard.stepMapping' },
  { key: 'validate', labelKey: 'importWizard.stepValidate' },
  { key: 'dryrun', labelKey: 'importWizard.stepDryRun' },
  { key: 'commit', labelKey: 'importWizard.stepCommit' },
  { key: 'summary', labelKey: 'importWizard.stepSummary' },
];

const ENTITY_KINDS = ['SELLER', 'SKU', 'OPENING_STOCK'];

/**
 * CSV only. Every step maps 1:1 to a real backend call in
 * packages/operations/src/services/imports.ts - this wizard has no client
 * logic that duplicates what the server already decides (conflict
 * detection, dry-run projection, commit idempotency).
 *
 * UPDATE_EXISTING/CREATE_NEW are REAL merge resolutions as of ADR-076 Part
 * B (they previously behaved as SKIP at commit - ADR-062 §5's original,
 * now-superseded gap): UPDATE_EXISTING updates the named existing entity's
 * safely-updatable fields in place; CREATE_NEW is accepted only for
 * DUPLICATE_BARCODE and rejected elsewhere (IMPORT.CREATE_NEW_NOT_ALLOWED)
 * because it would defeat the conflict's own point for OWNER_EXISTS/
 * SKU_EXISTS/LOCATION_EXISTS. The banner below states the CURRENT real
 * behaviour, not the old placeholder text - a stale "there is no merge
 * engine yet" claim left in this screen after Part B shipped was found and
 * fixed in the same pass as this epic's Part A/C/D work (apps/ops/
 * __tests__/importWizard.test.tsx updated to match).
 */
export function ImportWizardPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(id ? 'validate' : 'upload');
  const [batchId, setBatchId] = useState<string | null>(id ?? null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const { t, locale } = useLanguage();
  // Excel template path for products (separate from the CSV steps below, which are untouched).
  const [searchParams] = useSearchParams();
  const [excel, setExcel] = useState(!id && searchParams.get('mode') === 'excel');

  const [entityKind, setEntityKind] = useState('SELLER');
  const [fileName, setFileName] = useState('');
  const [content, setContent] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<Record<string, string>>({});
  const [mappingSpec, setMappingSpec] = useState<Record<string, string>>({});
  const [validation, setValidation] = useState<Record<string, unknown> | null>(null);
  const [dryRun, setDryRun] = useState<Record<string, unknown> | null>(null);
  const [commitResult, setCommitResult] = useState<Record<string, unknown> | null>(null);

  function onFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setContent(String(reader.result ?? ''));
    reader.readAsText(file);
  }

  async function doUpload(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = (await endpoints.createImportBatch({ source: 'CSV', entityKind, fileName, content })) as Record<string, unknown>;
      setBatchId(String(res.batchId));
      setHeaders((res.headers as string[]) ?? []);
      setSuggestions((res.suggestions as Record<string, string>) ?? {});
      setMappingSpec((res.suggestions as Record<string, string>) ?? {});
      setStep('mapping');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function doMapping(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await endpoints.applyImportMapping({ batchId: batchId!, spec: mappingSpec });
      setStep('validate');
      await doValidate();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function doValidate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await endpoints.validateImport(batchId!);
      setValidation(res);
      setStep('validate');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function resolve(rowNumber: number, resolution: 'SKIP' | 'UPDATE_EXISTING' | 'CREATE_NEW' | 'FAIL'): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await endpoints.resolveImportConflict({ batchId: batchId!, rowNumber, resolution });
      await doValidate();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function doDryRun(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await endpoints.dryRunImport(batchId!);
      setDryRun(res);
      setStep('dryrun');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function doCommit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await endpoints.commitImport(batchId!);
      setCommitResult(res);
      setStep('summary');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const conflicts = (validation?.issues as { rowNumber: number; code: string; params: Record<string, unknown> }[] | undefined) ?? [];
  const openConflicts = Number(validation?.conflicts ?? 0);

  if (excel) return <SkuExcel onBack={() => navigate('/imports')} />;

  return (
    <div>
      <div className="page-header">
        <h1>{t('importWizard.title')}</h1>
      </div>
      <div className="wizard-steps">
        {STEPS.map((s, i) => (
          <div key={s.key} className={`step ${s.key === step ? 'active' : i < STEPS.findIndex((x) => x.key === step) ? 'done' : ''}`}>
            {t(s.labelKey)}
          </div>
        ))}
      </div>

      {Boolean(error) && <ErrorBanner error={error} onRetry={() => setError(null)} />}

      {step === 'upload' && (
        <div className="card">
          <div className="field-row">
            <button type="button" onClick={() => setExcel(true)}>
              {t('imports.fromExcel')}
            </button>
            <span className="sub">{t('skuImport.excelCardHint')}</span>
          </div>
          <div className="field-row">
            <label htmlFor="entity-kind">{t('importWizard.whatAreYouImporting')}</label>
            <select id="entity-kind" value={entityKind} onChange={(e) => setEntityKind(e.target.value)}>
              {ENTITY_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>
          <div className="field-row">
            <label htmlFor="file-input">{t('importWizard.csvFile')}</label>
            <input id="file-input" type="file" accept=".csv,text/csv" onChange={onFile} />
          </div>
          <div className="form-actions">
            <button type="button" className="primary" disabled={busy || !content} onClick={() => void doUpload()}>
              {busy ? t('importWizard.uploading') : t('importWizard.uploadAndParse')}
            </button>
          </div>
        </div>
      )}

      {step === 'mapping' && (
        <div className="card">
          <p className="sub">{t('importWizard.mappingHint')}</p>
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('importWizard.colSourceColumn')}</th>
                <th>{t('importWizard.colSample')}</th>
                <th>{t('importWizard.colCanonicalField')}</th>
              </tr>
            </thead>
            <tbody>
              {headers.map((h) => (
                <tr key={h}>
                  <td>{h}</td>
                  <td className="sub">{suggestions[h] ? t('importWizard.suggested') : t('importWizard.unmapped')}</td>
                  <td>
                    <input
                      value={mappingSpec[h] ?? ''}
                      onChange={(e) => setMappingSpec((m) => ({ ...m, [h]: e.target.value }))}
                      placeholder="e.g. sku.code"
                      aria-label={t('importWizard.canonicalFieldFor', { column: h })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="form-actions">
            <button type="button" className="primary" disabled={busy} onClick={() => void doMapping()}>
              {busy ? t('importWizard.applying') : t('importWizard.applyMappingAndValidate')}
            </button>
          </div>
        </div>
      )}

      {step === 'validate' && (
        <div className="card">
          {validation ? (
            <>
              <div className="grid-cards" style={{ marginBottom: 12 }}>
                <div className="stat-card">
                  <div className="n">{String(validation.validRows)}</div>
                  <div className="label">{t('importWizard.validRows')}</div>
                </div>
                <div className="stat-card">
                  <div className="n">{String(validation.invalidRows)}</div>
                  <div className="label">{t('importWizard.invalidRows')}</div>
                </div>
                <div className="stat-card">
                  <div className="n">{formatNumber(openConflicts, locale)}</div>
                  <div className="label">{t('importWizard.openConflicts')}</div>
                </div>
              </div>
              {openConflicts > 0 && (
                <div className="error-banner" role="status">
                  {t('importWizard.conflictBannerText')}
                </div>
              )}
              {conflicts.length === 0 ? (
                <p>{t('importWizard.noIssuesFound')}</p>
              ) : (
                <div className="table-scroll"><table>
                  <thead>
                    <tr>
                      <th>{t('importWizard.colRow')}</th>
                      <th>{t('billing.colCode')}</th>
                      <th>{t('importWizard.colDetails')}</th>
                      <th>{t('importWizard.colResolve')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conflicts.map((c, i) => (
                      <tr key={i}>
                        <td>{c.rowNumber}</td>
                        <td>{c.code}</td>
                        <td>{JSON.stringify(c.params)}</td>
                        <td>
                          <select
                            defaultValue=""
                            disabled={busy}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v) void resolve(c.rowNumber, v as 'SKIP' | 'UPDATE_EXISTING' | 'CREATE_NEW' | 'FAIL');
                            }}
                          >
                            <option value="" disabled>
                              {t('importWizard.choose')}
                            </option>
                            <option value="SKIP">{t('importWizard.skipRow')}</option>
                            <option value="UPDATE_EXISTING">{t('importWizard.updateExisting')}</option>
                            <option value="CREATE_NEW">{t('importWizard.createNew')}</option>
                            <option value="FAIL">{t('importWizard.markHardError')}</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
              <div className="form-actions">
                <button type="button" onClick={() => void doValidate()} disabled={busy}>
                  {t('importWizard.revalidate')}
                </button>
                <button type="button" className="primary" disabled={busy || openConflicts > 0} onClick={() => void doDryRun()}>
                  {t('importWizard.continueToDryRun')}
                </button>
              </div>
            </>
          ) : (
            <button type="button" className="primary" onClick={() => void doValidate()} disabled={busy}>
              {t('importWizard.runValidation')}
            </button>
          )}
        </div>
      )}

      {step === 'dryrun' && dryRun && (
        <div className="card">
          <p className="sub">{t('importWizard.dryRunHint')}</p>
          <div className="grid-cards">
            <div className="stat-card">
              <div className="n">{String(dryRun.validRows)}</div>
              <div className="label">{t('importWizard.rowsValid')}</div>
            </div>
            <div className="stat-card">
              <div className="n">{String(dryRun.conflicts)}</div>
              <div className="label">{t('importWizard.conflicts')}</div>
            </div>
            <div className="stat-card">
              <div className="n">{Object.values((dryRun.willCreate as Record<string, number>) ?? {}).reduce((a, b) => a + b, 0)}</div>
              <div className="label">{t('importWizard.entitiesToCreate')}</div>
            </div>
            <div className="stat-card">
              <div className="n">{(dryRun.plannedMovements as unknown[])?.length ?? 0}</div>
              <div className="label">{t('importWizard.openingStockMovements')}</div>
            </div>
            <div className="stat-card">
              <div className="n">{String(dryRun.willSkip)}</div>
              <div className="label">{t('importWizard.rowsToSkip')}</div>
            </div>
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '16px 0' }}>
            <input type="checkbox" id="confirm-commit" />
            {t('importWizard.confirmCommitLabel')}
          </label>
          <div className="form-actions">
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => {
                const box = document.getElementById('confirm-commit') as HTMLInputElement | null;
                if (box?.checked) void doCommit();
              }}
            >
              {busy ? t('importWizard.committing') : t('importWizard.commitImport')}
            </button>
          </div>
        </div>
      )}

      {step === 'summary' && commitResult && (
        <div className="card">
          <div className="badge ok" style={{ marginBottom: 12 }}>
            {commitResult.replayed ? t('importWizard.alreadyCommitted') : t('importWizard.committed')}
          </div>
          <dl className="kv">
            <dt>{t('importWizard.created')}</dt>
            <dd>{String(commitResult.created)}</dd>
            <dt>{t('importWizard.skipped')}</dt>
            <dd>{String(commitResult.skipped)}</dd>
            <dt>{t('importWizard.failed')}</dt>
            <dd>{String(commitResult.failed)}</dd>
          </dl>
          <div className="form-actions">
            <button type="button" onClick={() => navigate('/imports')}>
              {t('importWizard.backToImports')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
